import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { backofficeApi } from '@mehir/api/backoffice-api';
import { useAppDispatch } from '@app/hooks/redux';
import { useAuth } from '@app/hooks/useAuth';
import {
  canAccess,
  flattenRolePermissions,
  mergeGrants,
  type PermissionGrant,
  type PermissionRequirement,
} from './permissions';

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface PermissionContextValue {
  grants: PermissionGrant[];
  status: Status;
  can: (requirement: PermissionRequirement | null | undefined) => boolean;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

export const PermissionProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const { token, roles } = useAuth();
  const [grants, setGrants] = useState<PermissionGrant[]>([]);
  const [status, setStatus] = useState<Status>('idle');

  // Roles arrive as a fresh array on every render; key on the contents so the
  // effect does not re-run for an identical role set.
  const rolesKey = roles.join('|');

  useEffect(() => {
    if (!token) {
      setGrants([]);
      setStatus('idle');
      return;
    }

    const roleNames = rolesKey.split('|').filter(Boolean);
    if (roleNames.length === 0) {
      setGrants([]);
      setStatus('ready');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    const subscriptions = roleNames.map((roleName) =>
      dispatch(backofficeApi.endpoints.getRolePermissionsByRoleNameAsync.initiate({ roleName }))
    );

    Promise.all(subscriptions)
      .then((results) => {
        if (cancelled) return;
        const failed = results.some((result) => result.error);
        if (failed) {
          // Fail-open is deliberate (see below), but it must not be silent.
          console.warn(
            '[permissions] could not load grants for one or more roles; access checks are disabled',
            results.filter((result) => result.error).map((result) => result.error)
          );
        }
        setGrants(mergeGrants(results.map((result) => flattenRolePermissions(result.data))));
        setStatus(failed ? 'error' : 'ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      subscriptions.forEach((subscription) => subscription.unsubscribe());
    };
  }, [dispatch, rolesKey, token]);

  const value = useMemo<PermissionContextValue>(() => {
    // Fail open when we could not build a grant set at all — a backend hiccup
    // or an unmapped role must not lock an administrator out of their own
    // panel. The server is still the enforcement point; this layer exists to
    // stop showing people screens they cannot use. Flip to `false` once every
    // production role is known to carry explicit grants.
    const hasUsableGrants = status === 'ready' && grants.length > 0;

    return {
      grants,
      status,
      can: (requirement) => (hasUsableGrants ? canAccess(grants, requirement) : true),
    };
  }, [grants, status]);

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};

export const usePermissions = (): PermissionContextValue => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};
